import React, { useState, useRef, useEffect } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from '../api/axios';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Load Google Fonts
const loadGoogleFonts = () => {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Roboto:wght@400;500;700&family=Montserrat:wght@400;600;700&family=Open+Sans:wght@400;600;700&family=Lato:wght@400;700&family=Poppins:wght@400;600;700&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&display=swap';
  link.rel = 'stylesheet';
  if (!document.querySelector(`link[href="${link.href}"]`)) {
    document.head.appendChild(link);
  }
};
loadGoogleFonts();

const customStyle = `
  .ql-container {
    height: 100% !important;
    min-height: 50px;
    font-size: inherit;
    border: none !important;
    box-sizing: border-box !important;
  }
  .text-box-wrapper .ql-container.ql-snow {
    border: none !important;
  }
  .ql-editor {
    overflow-y: visible !important;
    height: 100% !important;
    padding: 0px !important;
    min-height: 0 !important;
    box-sizing: border-box !important;
  }
  
  /* Make sure quill inherits the wrapper font family */
  .text-box-wrapper .ql-editor {
    font-family: inherit !important;
  }
  
  /* Make sure the active box editor doesn't get pushed down */
  .text-box-wrapper .ql-container {
    margin-top: 0;
  }
  
  /* Make images fill the text box so they can be resized using the box's resize handle */
  .text-box-wrapper img {
    width: 100%;
    height: 100%;
    display: block;
  }
  
  /* Prevent page scrolling when resizing elements */
  body.is-resizing {
    overflow: hidden !important;
    touch-action: none !important;
    -webkit-overflow-scrolling: none !important;
  }
  
  /* Hide the global Quill toolbar - we use per-textbox toolbars instead */
  .ql-toolbar {
    display: none !important;
  }
`;

export function ProposalGenerator({ isOpen, onClose, onSuccess, proposalId, readOnly = false, initialTab = 'form' }) {
  const [numPages, setNumPages] = useState(null);
  const [isLoadingProposal, setIsLoadingProposal] = useState(false);

  // Text boxes state: { id, pageId, type, text, x, y, width, height, fontSize, data? }
  const [textBoxes, setTextBoxes] = useState([]);
  const [activeBoxId, setActiveBoxId] = useState(null);
  const [editingBoxId, setEditingBoxId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedPageToAdd, setSelectedPageToAdd] = useState(0);

  const [isDragging, setIsDragging] = useState(false);
  const draggingRef = useRef(false); // synchronous ref to prevent scroll flicker
  const dragStartPos = useRef({ x: 0, y: 0 });
  const quillRef = useRef(null);
  const [rotationTooltip, setRotationTooltip] = useState({ visible: false, angle: 0 });
  const [activeHandle, setActiveHandle] = useState(null); // 'rotate' | 'move' | null
  
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isHistoryUpdate = useRef(false);

  useEffect(() => {
    if (isHistoryUpdate.current) {
      isHistoryUpdate.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (isDragging) return;
      setHistory(prev => {
        const currentHistory = prev.slice(0, historyIndex + 1);
        if (currentHistory.length > 0 && JSON.stringify(currentHistory[currentHistory.length - 1]) === JSON.stringify(textBoxes)) {
          return prev;
        }
        const newHistory = [...currentHistory, textBoxes];
        if (newHistory.length > 50) newHistory.shift();
        setHistoryIndex(newHistory.length - 1);
        return newHistory;
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [textBoxes, isDragging, historyIndex]);

  const [pageSequence, setPageSequence] = useState([]);
  const [draggedPageIndex, setDraggedPageIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleUndo = () => {
    if (historyIndex > 0) {
      isHistoryUpdate.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setTextBoxes(history[newIndex]);
      setActiveBoxId(null);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      isHistoryUpdate.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setTextBoxes(history[newIndex]);
      setActiveBoxId(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Clear defaults or previous session
      setTextBoxes([]);
      setPageSequence([]);
      setSelectedPageToAdd(0);
      setActiveBoxId(null);
      setEditingBoxId(null);
      
      // Start loading when modal opens with minimum 5 seconds
      setIsLoadingProposal(true);
      const loadingStartTime = Date.now();
      
      if (proposalId) {
        axios.get(`/proposals/${proposalId}?t=${Date.now()}`).then(res => {
          if (res.data && res.data.pdfEditorState) {
            const { savedTextBoxes, savedPageSequence } = res.data.pdfEditorState;
            if (savedTextBoxes) setTextBoxes(savedTextBoxes);
            if (savedPageSequence) setPageSequence(savedPageSequence);
          }
          // Don't set loading to false here - wait for PDF to load
        }).catch(err => {
          console.error("Error fetching proposal state:", err);
          // Even on error, wait minimum 5 seconds
          const elapsedTime = Date.now() - loadingStartTime;
          const remainingTime = Math.max(0, 5000 - elapsedTime);
          setTimeout(() => {
            setIsLoadingProposal(false);
          }, remainingTime);
        });
      }
      // If no proposalId, loading will be disabled when PDF loads (minimum 5 seconds)
    } else {
      // Reset loading state when modal closes
      setIsLoadingProposal(false);
    }
  }, [isOpen, proposalId]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    if (pageSequence.length === 0) {
      setPageSequence(Array.from(new Array(numPages), (_, i) => ({
        id: `page-${i + 1}-${Date.now()}`,
        originalPageNumber: i + 1
      })));
    }
    // Hide loading spinner after minimum 5 seconds from modal open
    setTimeout(() => {
      setIsLoadingProposal(false);
    }, 5000);
  };

  const activeModules = React.useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['image'],
      ['clean']
    ],
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true
    }
  }), []);

  const getSpawnY = () => {
    const container = document.getElementById('pdf-scroll-container');
    const pageObj = pageSequence[selectedPageToAdd];
    if (!pageObj) return 100;
    const pageEl = document.getElementById(`pdf-page-container-${pageObj.id}`);
    if (container && pageEl) {
      const pageRect = pageEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (pageRect.top < containerRect.top) {
        return (containerRect.top - pageRect.top) + 100;
      }
    }
    return 100;
  };

  const addTextBox = (e) => {
    if (e) e.stopPropagation();
    if (!pageSequence[selectedPageToAdd]) return;

    // Get page dimensions to center the text box
    const pageObj = pageSequence[selectedPageToAdd];
    const pageEl = document.getElementById(`pdf-page-container-${pageObj.id}`);
    let centerX = 100; // fallback
    
    if (pageEl) {
      const pageWidth = pageEl.offsetWidth || 800; // fallback to standard width
      const textBoxWidth = 200;
      centerX = (pageWidth - textBoxWidth) / 2; // Center horizontally
    }

    const newBox = {
      id: Date.now().toString(),
      type: 'text',
      pageId: pageSequence[selectedPageToAdd].id,
      text: '<p>New Text Box</p>',
      x: centerX,
      y: getSpawnY(),
      width: 200,
      height: 'auto',
      fontSize: 16
    };
    setTextBoxes(prev => [...prev, newBox]);
    setActiveBoxId(newBox.id);
  };

  const imageFileInputRef = useRef(null);

  const addImageBox = (e) => {
    if (e) e.stopPropagation();
    if (imageFileInputRef.current) {
      imageFileInputRef.current.click();
    }
  };

  const handleImageUpload = (e) => {
    if (!pageSequence[selectedPageToAdd]) return;
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      
      // Get page dimensions to center the image box
      const pageObj = pageSequence[selectedPageToAdd];
      const pageEl = document.getElementById(`pdf-page-container-${pageObj.id}`);
      let centerX = 100; // fallback
      
      if (pageEl) {
        const pageWidth = pageEl.offsetWidth || 800;
        const imageBoxWidth = 400;
        centerX = (pageWidth - imageBoxWidth) / 2; // Center horizontally
      }
      
      const newBox = {
        id: Date.now().toString(),
        type: 'text',
        pageId: pageSequence[selectedPageToAdd].id,
        text: `<img src="${base64}" />`,
        x: centerX, 
        y: getSpawnY(), 
        width: 400, 
        height: 300, 
        fontSize: 16
      };
      setTextBoxes(prev => [...prev, newBox]);
      setActiveBoxId(newBox.id);
      e.target.value = null;
    };
    reader.readAsDataURL(file);
  };

  const addTableBox = (e) => {
    if (e) e.stopPropagation();
    if (!pageSequence[selectedPageToAdd]) return;
    const newBox = {
      id: Date.now().toString(),
      type: 'table',
      pageId: pageSequence[selectedPageToAdd].id,
      data: Array.from({ length: 3 }, () => Array(3).fill('')),
      x: 100, y: getSpawnY(), width: 500, height: 200, fontSize: 16
    };
    setTextBoxes(prev => [...prev, newBox]);
    setActiveBoxId(newBox.id);
  };

  const duplicatePage = () => {
    const pageToDuplicate = pageSequence[selectedPageToAdd];
    if (!pageToDuplicate) return;

    const newPageId = `page-${pageToDuplicate.originalPageNumber}-${Date.now()}`;
    const newPage = {
      id: newPageId,
      originalPageNumber: pageToDuplicate.originalPageNumber
    };

    // Clone text boxes belonging to this page
    const boxesToClone = textBoxes.filter(b => b.pageId === pageToDuplicate.id);
    const clonedBoxes = boxesToClone.map(b => ({
      ...b,
      id: Date.now().toString() + Math.random().toString(),
      pageId: newPageId
    }));

    const newSequence = [...pageSequence];
    newSequence.splice(selectedPageToAdd + 1, 0, newPage);
    setPageSequence(newSequence);
    setTextBoxes(prev => [...prev, ...clonedBoxes]);
    setSelectedPageToAdd(selectedPageToAdd + 1);
  };

  const deletePage = () => {
    if (pageSequence.length <= 1) {
      alert("Cannot delete the last remaining page.");
      return;
    }
    const pageToDelete = pageSequence[selectedPageToAdd];
    if (!pageToDelete) return;

    // Remove text boxes belonging to this page
    setTextBoxes(prev => prev.filter(b => b.pageId !== pageToDelete.id));

    const newSequence = [...pageSequence];
    newSequence.splice(selectedPageToAdd, 1);
    setPageSequence(newSequence);
    setSelectedPageToAdd(Math.max(0, selectedPageToAdd - 1));
  };

  const updateTextBox = (id, field, value) => {
    setTextBoxes(prev => prev.map(box => box.id === id ? { ...box, [field]: value } : box));
  };

  const handlePointerMove = (e) => {
    // Movement is handled by native document.addEventListener('pointermove', ...) 
    // inside each element's onPointerDown. This global handler is intentionally a no-op
    // to prevent double-application of dx/dy which cancels out horizontal movement.
    return;
  };

  const handlePointerUp = (e) => {
    setIsDragging(false);
    if (e && e.target && e.target.releasePointerCapture && e.pointerId !== undefined) {
      try { e.target.releasePointerCapture(e.pointerId); } catch(err) {}
    }
  };

  const generateFinalPDF = async () => {
    if (!pageSequence.length) return;
    setIsGenerating(true);
    try {
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: 'a4'
      });

      for (let i = 0; i < pageSequence.length; i++) {
        const pageObj = pageSequence[i];
        const pageElement = document.getElementById(`pdf-page-container-${pageObj.id}`);
        if (!pageElement) continue;

        const oldActiveBoxId = activeBoxId;
        setActiveBoxId(null);
        setEditingBoxId(null);

        await new Promise(r => setTimeout(r, 100));

        const canvas = await html2canvas(pageElement, {
          scale: 2,
          useCORS: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.75);

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        if (i === 0) {
          if (canvas.width > canvas.height) {
            pdf.deletePage(1);
            pdf.addPage([pdfWidth, pdfHeight], 'l');
          }
        } else {
          pdf.addPage([pdfWidth, pdfHeight], canvas.width > canvas.height ? 'l' : 'p');
        }

        const actualPdfWidth = pdf.internal.pageSize.getWidth();

        pdf.addImage(imgData, 'JPEG', 0, 0, actualPdfWidth, (canvas.height * actualPdfWidth) / canvas.width);

        setActiveBoxId(oldActiveBoxId);
      }

      const base64Pdf = pdf.output('datauristring');

      const pdfEditorState = {
        savedTextBoxes: textBoxes,
        savedPageSequence: pageSequence,
        base64Pdf: base64Pdf
      };

      if (proposalId) {
          try {
              await axios.put(`/proposals/${proposalId}`, { pdfEditorState });
              if (onSuccess) onSuccess();
          } catch (e) {
              console.error('Error saving state:', e);
              alert('Error saving the proposal. The PDF might be too large.');
          }
      } else {
          // Create record in database
          const proposalData = {
              title: 'Custom Generated Proposal',
              clientName: 'TBD (Add Client Details)',
              total: 0,
              status: 'draft',
              pdfEditorState
          };
          
          try {
              const res = await axios.post('/proposals', proposalData);
              if (onSuccess) {
                  onSuccess(res.data);
              }
          } catch (apiError) {
              console.error('Error creating proposal record:', apiError);
              alert('Failed to create record in database. The PDF might be too large.');
          }
      }
    } catch (err) {
      console.error(err);
      alert("Error generating PDF: " + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleScroll = (e) => {
    if (!pageSequence.length) return;
    let closestPage = 0;
    let minDistance = Infinity;

    for (let i = 0; i < pageSequence.length; i++) {
      const pageObj = pageSequence[i];
      const pageEl = document.getElementById(`pdf-page-container-${pageObj.id}`);
      if (pageEl) {
        const rect = pageEl.getBoundingClientRect();
        const pageCenter = rect.top + rect.height / 2;
        const distance = Math.abs(window.innerHeight / 2 - pageCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestPage = i;
        }
      }
    }

    if (closestPage !== selectedPageToAdd) {
      setSelectedPageToAdd(closestPage);
    }
  };

  if (!isOpen) return null;

  const renderWidth = typeof window !== 'undefined' ? window.innerWidth - 60 : 1000;

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onClick={(e) => {
        // Deselect if clicking outside any box, but don't deselect if clicking the toolbar
        if (!e.target.closest('.text-box-wrapper') && !e.target.closest('.ql-toolbar') && !e.target.closest('.editor-header')) {
          setActiveBoxId(null);
          setEditingBoxId(null);
        }
      }}
    >
      <style>{customStyle}</style>

      {/* Loading Spinner Overlay */}
      {isLoadingProposal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid #ffffff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: 600,
            marginTop: '20px',
            fontFamily: "'Inter', -apple-system, sans-serif"
          }}>
            {readOnly ? 'Loading Proposal...' : 'Loading Proposal for Editing...'}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header Bar */}
      <div className="editor-header" style={{ position: 'relative', zIndex: 10000, background: '#1f2937', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', color: 'white', letterSpacing: '1px' }}>
          {readOnly ? 'View Proposal' : 'Generate Proposal'}
        </h2>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {!readOnly && (
            <>
              <select
                value={selectedPageToAdd}
                onChange={(e) => setSelectedPageToAdd(Number(e.target.value))}
                style={{ padding: '6px', borderRadius: '4px', border: 'none', background: 'white', color: 'black' }}
              >
                {Array.from(new Array(numPages || 0), (_, i) => (
                  <option key={i} value={i}>Page {i + 1}</option>
                ))}
              </select>

              <div style={{ display: 'flex', gap: '8px', background: '#374151', padding: '4px', borderRadius: '4px' }}>
                <button onClick={addTextBox} style={{ padding: '6px 12px', background: '#10b981', color: 'white', border: textBoxes.find(b=>b.id===activeBoxId)?.type==='text' ? '2px solid #ffffff' : '2px solid transparent', borderRadius: '4px', cursor: 'pointer', boxShadow: textBoxes.find(b=>b.id===activeBoxId)?.type==='text' ? '0 0 8px rgba(16,185,129,0.8)' : 'none' }}>+ Text</button>
                <button onClick={addImageBox} style={{ padding: '6px 12px', background: '#8b5cf6', color: 'white', border: textBoxes.find(b=>b.id===activeBoxId)?.type==='image' ? '2px solid #ffffff' : '2px solid transparent', borderRadius: '4px', cursor: 'pointer', boxShadow: textBoxes.find(b=>b.id===activeBoxId)?.type==='image' ? '0 0 8px rgba(139,92,246,0.8)' : 'none' }}>+ Image</button>
                <button onClick={addTableBox} style={{ padding: '6px 12px', background: '#f59e0b', color: 'white', border: textBoxes.find(b=>b.id===activeBoxId)?.type==='table' ? '2px solid #ffffff' : '2px solid transparent', borderRadius: '4px', cursor: 'pointer', boxShadow: textBoxes.find(b=>b.id===activeBoxId)?.type==='table' ? '0 0 8px rgba(245,158,11,0.8)' : 'none' }}>+ Table</button>
                <button onClick={duplicatePage} style={{ padding: '6px 12px', background: '#14b8a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }} title="Duplicate Current Page">Copy Page</button>
                <button onClick={deletePage} style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }} title="Delete Current Page">Delete Page</button>
              </div>

              <div style={{ display: 'flex', gap: '8px', background: '#374151', padding: '4px', borderRadius: '4px', marginLeft: '8px' }}>
                <button
                  onClick={() => {
                    if (!activeBoxId) return;
                    const box = textBoxes.find(b => b.id === activeBoxId);
                    if (!box) return;

                    const renderWidth = typeof window !== 'undefined' ? window.innerWidth - 60 : 1000;
                    let nextAlign = 'center';
                    if (box.pageAlign === 'center') nextAlign = 'right';
                    else if (box.pageAlign === 'right') nextAlign = 'left';

                    let newX = box.x;
                    if (nextAlign === 'left') newX = 20;
                    else if (nextAlign === 'center') newX = (renderWidth - box.width) / 2;
                    else if (nextAlign === 'right') newX = renderWidth - box.width - 20;

                    setTextBoxes(prev => prev.map(b => b.id === box.id ? { ...b, pageAlign: nextAlign, x: newX } : b));
                  }}
                  disabled={!activeBoxId}
                  style={{ padding: '6px 12px', background: activeBoxId ? '#f59e0b' : '#4b5563', color: 'white', border: 'none', borderRadius: '4px', cursor: activeBoxId ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center' }}
                  title="Align Selected Box on Page"
                >
                  Alignment
                </button>
                <button onClick={handleUndo} style={{ padding: '6px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>⟲ Undo</button>
                <button onClick={handleRedo} style={{ padding: '6px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>⟳ Redo</button>
              </div>

              <button onClick={generateFinalPDF} disabled={isGenerating} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: isGenerating ? 0.7 : 1, marginLeft: '16px' }}>
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </>
          )}
          <button onClick={onClose} disabled={isGenerating} style={{ padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
        </div>
      </div>

      {/* Main PDF Viewer Area - Scrolling vertically */}
      <div
        id="pdf-scroll-container"
        onScroll={handleScroll}
        style={{ flex: 1, overflow: draggingRef.current ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#374151', padding: '20px', paddingLeft: '50px', touchAction: draggingRef.current ? 'none' : 'auto' }}
      >
        <Document
          file={process.env.PUBLIC_URL + '/BtplSoft_Proposal.pdf'}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div style={{ padding: '50px', color: 'white' }}>Loading PDF...</div>}
        >
          {pageSequence.map((pageObj, index) => {
            return (
              <div
                key={pageObj.id}
                id={`pdf-page-container-${pageObj.id}`}
                draggable
                onDragStart={(e) => {
                  setDraggedPageIndex(index);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverIndex(index);
                }}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverIndex(null);
                  if (draggedPageIndex === null) return;
                  const dropIndex = index;
                  if (draggedPageIndex === dropIndex) return;

                  const newSequence = [...pageSequence];
                  const [draggedPage] = newSequence.splice(draggedPageIndex, 1);
                  newSequence.splice(dropIndex, 0, draggedPage);
                  setPageSequence(newSequence);
                  setDraggedPageIndex(null);
                }}
                onDragEnd={() => {
                  setDraggedPageIndex(null);
                  setDragOverIndex(null);
                }}
                style={{
                  position: 'relative',
                  marginBottom: '30px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                  background: 'white',
                  width: renderWidth,
                  height: 'max-content',
                  border: draggedPageIndex === index ? '2px dashed #3b82f6' : '1px solid #d1d5db',
                  opacity: draggedPageIndex === index ? 0.5 : 1
                }}
              >
                {/* Visual Drop Indicator */}
                {dragOverIndex === index && draggedPageIndex !== index && (
                  <div style={{
                    position: 'absolute',
                    top: -15,
                    left: 0,
                    right: 0,
                    height: 8,
                    background: '#3b82f6',
                    borderRadius: 4,
                    zIndex: 100,
                    boxShadow: '0 4px 6px rgba(59, 130, 246, 0.5)'
                  }} />
                )}

                {/* Drag Handle */}
                <div style={{ position: 'absolute', top: 20, left: -40, width: 30, height: 30, background: '#4b5563', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', cursor: 'grab', zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} title="Drag to reorder page">
                  ☰
                </div>

                {/* PDF Page Background */}
                <Page
                  pageNumber={pageObj.originalPageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  width={renderWidth}
                />

                {/* Render Draggable Text Boxes for this page */}
                {textBoxes.filter(box => box.pageId === pageObj.id).map(box => (
                  <div
                    key={box.id}
                    className={`text-box-wrapper ${activeBoxId === box.id ? 'active' : 'inactive'}`}
                    style={{
                      position: 'absolute',
                      left: box.x,
                      top: box.y,
                      width: box.width,
                      height: box.type === 'text' ? 'auto' : (box.height || 'auto'),
                      transform: `rotate(${editingBoxId === box.id ? 0 : (box.rotation || 0)}deg)`,
                      transformOrigin: 'center center',
                      boxSizing: 'border-box',
                      fontSize: `${box.fontSize || 16}px`,
                      fontFamily: box.fontFamily || 'Arial, sans-serif',
                      border: (activeBoxId === box.id && !readOnly) ? '2px solid #8b3dff' : '2px solid transparent',
                      background: (activeBoxId === box.id && !readOnly) ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
                      zIndex: activeBoxId === box.id ? 20 : 10,
                      cursor: (activeBoxId === box.id && !readOnly) ? 'move' : 'default',
                      touchAction: 'none',
                    }}
                    onClick={(e) => { if (!readOnly) { e.stopPropagation(); setActiveBoxId(box.id); } }}
                    onPointerDown={(e) => {
                      if (readOnly) return;
                      if (['TEXTAREA', 'INPUT', 'SELECT', 'BUTTON', 'OPTION', 'TR', 'TD'].includes(e.target.tagName)) {
                        setActiveBoxId(box.id);
                        return;
                      }
                      if (editingBoxId === box.id) {
                        // If they are actively typing, let them click the text
                        return;
                      }
                      e.stopPropagation();
                      e.preventDefault();
                      
                      // Don't prevent scrolling during move - we want auto-scroll
                      // document.body.classList.add('is-resizing'); // REMOVED
                      
                      if (e.target.setPointerCapture && e.pointerId !== undefined) {
                        try { e.target.setPointerCapture(e.pointerId); } catch(err) {}
                      }
                      setActiveBoxId(box.id);
                      setIsDragging(true);
                      draggingRef.current = true;
                      
                      let lastX = e.clientX;
                      let lastY = e.clientY;
                      let autoScrollAnimationId = null;
                      
                      // Auto-scroll function using requestAnimationFrame
                      const performAutoScroll = () => {
                        const mouseY = lastY;
                        const viewportHeight = window.innerHeight;
                        const scrollThreshold = 200; // Increased for easier triggering
                        const toolbarHeight = 0; // No toolbar blocking now
                        
                        let shouldScroll = false;
                        
                        // Calculate distance-based speed (faster when closer to edge)
                        const calculateSpeed = (distance, threshold) => {
                          // Speed ranges from 8 (far) to 25 (close to edge)
                          const normalizedDistance = Math.max(0, Math.min(1, distance / threshold));
                          return Math.round(8 + (1 - normalizedDistance) * 17);
                        };
                        
                        // Scroll down when near bottom
                        if (mouseY > viewportHeight - scrollThreshold) {
                          const distanceFromBottom = viewportHeight - mouseY;
                          const scrollSpeed = calculateSpeed(distanceFromBottom, scrollThreshold);
                          window.scrollBy(0, scrollSpeed);
                          shouldScroll = true;
                        }
                        // Scroll up when near top (account for toolbar)
                        else if (mouseY < scrollThreshold + toolbarHeight) {
                          const currentScroll = document.documentElement.scrollTop || document.body.scrollTop || window.pageYOffset || 0;
                          if (currentScroll > 0) {
                            const distanceFromTop = mouseY - toolbarHeight;
                            const scrollSpeed = calculateSpeed(distanceFromTop, scrollThreshold);
                            window.scrollBy(0, -scrollSpeed);
                            // Force scroll using direct manipulation as fallback
                            document.documentElement.scrollTop = Math.max(0, currentScroll - scrollSpeed);
                            shouldScroll = true;
                          }
                        }
                        
                        // Continue animation if we're still in scroll zone
                        if (shouldScroll && draggingRef.current) {
                          autoScrollAnimationId = requestAnimationFrame(performAutoScroll);
                        } else {
                          autoScrollAnimationId = null;
                        }
                      };
                      
                      const onMove = (moveEvent) => {
                        moveEvent.preventDefault();
                        moveEvent.stopPropagation();
                        
                        const dx = moveEvent.clientX - lastX;
                        const dy = moveEvent.clientY - lastY;
                        console.log('Moving:', { dx, dy, newX: box.x + dx, newY: box.y + dy }); // DEBUG
                        setTextBoxes(prev => prev.map(b => b.id === box.id ? { ...b, x: b.x + dx, y: b.y + dy } : b));
                        lastX = moveEvent.clientX;
                        lastY = moveEvent.clientY;
                        
                        // Start or restart auto-scroll
                        const mouseY = moveEvent.clientY;
                        const viewportHeight = window.innerHeight;
                        const scrollThreshold = 200;
                        const toolbarHeight = 0; // No toolbar blocking now
                        
                        // Check if we're in a scroll zone (account for toolbar at top)
                        const inScrollZone = (mouseY < scrollThreshold + toolbarHeight) || (mouseY > viewportHeight - scrollThreshold);
                        
                        if (inScrollZone && !autoScrollAnimationId) {
                          // Start auto-scroll animation
                          autoScrollAnimationId = requestAnimationFrame(performAutoScroll);
                        } else if (!inScrollZone && autoScrollAnimationId) {
                          // Stop auto-scroll when leaving scroll zone
                          cancelAnimationFrame(autoScrollAnimationId);
                          autoScrollAnimationId = null;
                        }
                      };
                      
                      const onUp = (upEvent) => {
                        // Clear auto-scroll animation
                        if (autoScrollAnimationId) {
                          cancelAnimationFrame(autoScrollAnimationId);
                          autoScrollAnimationId = null;
                        }
                        
                        // Remove the class to re-enable scrolling
                        // document.body.classList.remove('is-resizing'); // REMOVED
                        
                        if (e.target.releasePointerCapture && upEvent.pointerId !== undefined) {
                          try { e.target.releasePointerCapture(upEvent.pointerId); } catch(err) {}
                        }
                        setIsDragging(false);
                        draggingRef.current = false;
                        document.removeEventListener('pointermove', onMove);
                        document.removeEventListener('pointerup', onUp);
                      };
                      
                      document.addEventListener('pointermove', onMove, { passive: false });
                      document.addEventListener('pointerup', onUp);
                    }}
                    onDoubleClick={(e) => {
                      if (box.type === 'text') {
                        e.stopPropagation();
                        setEditingBoxId(box.id);
                      }
                    }}
                  >
                    {activeBoxId === box.id && !isGenerating && !readOnly && (
                      <>
                        {/* Canva-Style Controls under the box */}
                        <div style={{ position: 'absolute', bottom: '-40px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px', zIndex: 101 }}>
                          {/* Rotate Handle */}
                          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                            <div
                              style={{
                                width: '28px', height: '28px',
                                background: activeHandle === 'rotate' ? '#3b82f6' : '#ffffff',
                                border: activeHandle === 'rotate' ? '2px solid #1d4ed8' : '1px solid #d1d5db',
                                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: activeHandle === 'rotate' ? '0 0 10px rgba(59,130,246,0.7)' : '0 2px 4px rgba(0,0,0,0.1)',
                                cursor: 'grab', touchAction: 'none',
                                transition: 'all 0.15s ease',
                                // Visual snap indicator
                                transform: rotationTooltip.isSnapped ? 'scale(1.2)' : 'scale(1)',
                                background: rotationTooltip.isSnapped ? '#10b981' : (activeHandle === 'rotate' ? '#3b82f6' : '#ffffff'),
                                border: rotationTooltip.isSnapped ? '2px solid #059669' : (activeHandle === 'rotate' ? '2px solid #1d4ed8' : '1px solid #d1d5db'),
                                boxShadow: rotationTooltip.isSnapped ? '0 0 20px rgba(16,185,129,0.8)' : (activeHandle === 'rotate' ? '0 0 10px rgba(59,130,246,0.7)' : '0 2px 4px rgba(0,0,0,0.1)')
                              }}
                              title="Rotate (Snap to 0° when close)"
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                
                                // Add class to body to prevent scrolling during rotation
                                document.body.classList.add('is-resizing');
                                
                                if (e.target.setPointerCapture && e.pointerId !== undefined) {
                                  try { e.target.setPointerCapture(e.pointerId); } catch(err) {}
                                }
                                setIsDragging(true);
                                draggingRef.current = true;
                                setActiveHandle('rotate');
                                
                                // Lock the element's center in PAGE coordinates at drag-start
                                const wrapperEl = e.target.closest('.text-box-wrapper');
                                const wrapperRect = wrapperEl.getBoundingClientRect();
                                const centerXClient = wrapperRect.left + wrapperRect.width / 2;
                                const centerYClient = wrapperRect.top + wrapperRect.height / 2;
                                
                                // Store the locked center relative to the page container
                                const pageContainer = wrapperEl.parentElement;
                                const pageRect = pageContainer.getBoundingClientRect();
                                const lockedCenterPageX = centerXClient - pageRect.left;
                                const lockedCenterPageY = centerYClient - pageRect.top;
                                
                                const startAngle = Math.atan2(e.clientY - centerYClient, e.clientX - centerXClient) * (180 / Math.PI);
                                const startRotation = box.rotation || 0;
                                
                                setRotationTooltip({ visible: true, angle: Math.round(startRotation) });

                                const onMove = (moveEvent) => {
                                  moveEvent.preventDefault();
                                  moveEvent.stopPropagation();
                                  const currentAngle = Math.atan2(moveEvent.clientY - centerYClient, moveEvent.clientX - centerXClient) * (180 / Math.PI);
                                  let delta = currentAngle - startAngle;
                                  let newRotation = (startRotation + delta) % 360;
                                  
                                  // Normalize rotation to -180 to 180 range
                                  if (newRotation > 180) newRotation -= 360;
                                  if (newRotation < -180) newRotation += 360;
                                  
                                  // Snap to straight (0°) when within 5 degrees
                                  const snapThreshold = 5;
                                  let isSnapped = false;
                                  
                                  if (Math.abs(newRotation) <= snapThreshold) {
                                    newRotation = 0;
                                    isSnapped = true;
                                    
                                    // Haptic feedback on mobile devices
                                    if (navigator.vibrate) {
                                      navigator.vibrate(50); // 50ms vibration
                                    }
                                  }
                                  
                                  if (moveEvent.shiftKey) {
                                    newRotation = Math.round(newRotation / 45) * 45;
                                  }
                                  
                                  setRotationTooltip({ 
                                    visible: true, 
                                    angle: Math.round(newRotation),
                                    isSnapped: isSnapped // Pass snap status to tooltip
                                  });
                                  
                                  // Recalculate x/y so the center stays at lockedCenter
                                  setTextBoxes(prev => prev.map(b => {
                                    if (b.id !== box.id) return b;
                                    const newX = lockedCenterPageX - b.width / 2;
                                    const newY = lockedCenterPageY - (b.height || 40) / 2;
                                    return { ...b, rotation: newRotation, x: newX, y: newY };
                                  }));
                                };

                                const onUp = (upEvent) => {
                                  // Remove the class to re-enable scrolling
                                  document.body.classList.remove('is-resizing');
                                  
                                  if (e.target.releasePointerCapture && upEvent.pointerId !== undefined) {
                                    try { e.target.releasePointerCapture(upEvent.pointerId); } catch(err) {}
                                  }
                                  setIsDragging(false);
                                  draggingRef.current = false;
                                  setActiveHandle(null);
                                  setRotationTooltip({ visible: false, angle: 0 });
                                  document.removeEventListener('pointermove', onMove);
                                  document.removeEventListener('pointerup', onUp);
                                };

                                document.addEventListener('pointermove', onMove, { passive: false });
                                document.addEventListener('pointerup', onUp);
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={activeHandle === 'rotate' ? '#ffffff' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"></path><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 2v6h6"></path></svg>
                            </div>
                            {rotationTooltip.visible && activeBoxId === box.id && (
                              <div style={{ position: 'absolute', top: '-34px', background: '#1f2937', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                {rotationTooltip.angle}°
                              </div>
                            )}
                          </div>
                          
                          {/* Move Handle */}
                          <div
                            style={{
                              width: '28px', height: '28px',
                              background: activeHandle === 'move' ? '#10b981' : '#ffffff',
                              border: activeHandle === 'move' ? '2px solid #059669' : '1px solid #d1d5db',
                              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: activeHandle === 'move' ? '0 0 10px rgba(16,185,129,0.7)' : '0 2px 4px rgba(0,0,0,0.1)',
                              cursor: 'move', touchAction: 'none',
                              transition: 'all 0.15s ease'
                            }}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (e.target.setPointerCapture && e.pointerId !== undefined) {
                                try { e.target.setPointerCapture(e.pointerId); } catch(err) {}
                              }
                              setActiveBoxId(box.id);
                              setIsDragging(true);
                              draggingRef.current = true;
                              setActiveHandle('move');
                              
                              let lastX = e.clientX;
                              let lastY = e.clientY;
                              
                              const onMove = (moveEvent) => {
                                moveEvent.preventDefault();
                                const dx = moveEvent.clientX - lastX;
                                const dy = moveEvent.clientY - lastY;
                                setTextBoxes(prev => prev.map(b => b.id === box.id ? { ...b, x: b.x + dx, y: b.y + dy } : b));
                                lastX = moveEvent.clientX;
                                lastY = moveEvent.clientY;
                              };
                              
                              const onUp = (upEvent) => {
                                if (e.target.releasePointerCapture && upEvent.pointerId !== undefined) {
                                  try { e.target.releasePointerCapture(upEvent.pointerId); } catch(err) {}
                                }
                                setIsDragging(false);
                                draggingRef.current = false;
                                setActiveHandle(null);
                                document.removeEventListener('pointermove', onMove);
                                document.removeEventListener('pointerup', onUp);
                              };
                              
                              document.addEventListener('pointermove', onMove, { passive: false });
                              document.addEventListener('pointerup', onUp);
                            }}
                            title="Drag to move"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={activeHandle === 'move' ? '#ffffff' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9l-3 3 3 3"></path><path d="M9 5l3-3 3 3"></path><path d="M19 9l3 3-3 3"></path><path d="M9 19l3 3 3-3"></path><line x1="2" y1="12" x2="22" y2="12"></line><line x1="12" y1="2" x2="12" y2="22"></line></svg>
                          </div>
                        </div>

                        {/* Render Resize Handles */}
                        {[
                          { dir: 'nw', style: { top: '-7px', left: '-7px', cursor: 'nw-resize' }, type: 'corner' },
                          { dir: 'ne', style: { top: '-7px', right: '-7px', cursor: 'ne-resize' }, type: 'corner' },
                          { dir: 'sw', style: { bottom: '-7px', left: '-7px', cursor: 'sw-resize' }, type: 'corner' },
                          { dir: 'se', style: { bottom: '-7px', right: '-7px', cursor: 'se-resize' }, type: 'corner' },
                          { dir: 'w', style: { top: '50%', left: '-5px', transform: 'translateY(-50%)', cursor: 'ew-resize', height: '20px', width: '8px', borderRadius: '4px' }, type: 'side' },
                          { dir: 'e', style: { top: '50%', right: '-5px', transform: 'translateY(-50%)', cursor: 'ew-resize', height: '20px', width: '8px', borderRadius: '4px' }, type: 'side' },
                        ].map(handle => (
                          <div
                            key={handle.dir}
                            style={{
                              position: 'absolute',
                              width: handle.type === 'corner' ? '14px' : handle.style.width,
                              height: handle.type === 'corner' ? '14px' : handle.style.height,
                              background: '#ffffff',
                              borderRadius: handle.type === 'corner' ? '50%' : handle.style.borderRadius,
                              zIndex: 100,
                              border: '1px solid #9ca3af',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                              touchAction: 'none',
                              ...handle.style
                            }}
                            onPointerDown={(e) => {
                              e.preventDefault(); // Prevent page scroll/movement
                              e.stopPropagation();
                              
                              // Add class to body to prevent scrolling during resize
                              document.body.classList.add('is-resizing');
                              
                              // Capture the pointer to ensure smooth dragging
                              if (e.target.setPointerCapture && e.pointerId !== undefined) {
                                try { e.target.setPointerCapture(e.pointerId); } catch(err) {}
                              }
                              
                              setIsDragging(true);
                              draggingRef.current = true;
                              const startX = e.clientX;
                              const startY = e.clientY;
                              const startWidth = box.width;
                              const startHeight = box.height || (box.type === 'image' ? 200 : 30);
                              const startBoxX = box.x;
                              const startBoxY = box.y;

                              const onMove = (moveEvent) => {
                                moveEvent.preventDefault(); // Always prevent default to stop page scrolling
                                moveEvent.stopPropagation();
                                
                                let dx = moveEvent.clientX - startX;
                                let dy = moveEvent.clientY - startY;

                                const rot = box.rotation || 0;
                                if (rot !== 0) {
                                  const rad = -rot * (Math.PI / 180);
                                  const rotatedDx = dx * Math.cos(rad) - dy * Math.sin(rad);
                                  const rotatedDy = dx * Math.sin(rad) + dy * Math.cos(rad);
                                  dx = rotatedDx;
                                  dy = rotatedDy;
                                }

                                let newWidth = startWidth;
                                let newHeight = startHeight;
                                let newX = startBoxX;
                                let newY = startBoxY;
                                let newFontSize = box.fontSize || 16;

                                if (handle.dir.includes('e')) newWidth = Math.max(50, startWidth + dx);
                                if (handle.dir.includes('w')) {
                                  newWidth = Math.max(50, startWidth - dx);
                                  newX = startBoxX + (startWidth - newWidth);
                                }

                                if (handle.type === 'corner') {
                                  const scale = newWidth / startWidth;
                                  newHeight = Math.max(30, startHeight * scale);
                                  if (handle.dir.includes('n')) {
                                    newY = startBoxY + (startHeight - newHeight);
                                  }
                                  if (box.type === 'text' || box.type === 'table') {
                                    newFontSize = Math.max(8, Math.round((box.fontSize || 16) * scale));
                                  }
                                } else {
                                  if (handle.dir.includes('s')) newHeight = Math.max(30, startHeight + dy);
                                  if (handle.dir.includes('n')) {
                                    newHeight = Math.max(30, startHeight - dy);
                                    newY = startBoxY + (startHeight - newHeight);
                                  }
                                }

                                setTextBoxes(prev => prev.map(b => b.id === box.id ? { ...b, width: newWidth, height: newHeight, x: newX, y: newY, fontSize: newFontSize } : b));
                              };
                              const onUp = (upEvent) => {
                                // Remove the class to re-enable scrolling
                                document.body.classList.remove('is-resizing');
                                
                                if (e.target.releasePointerCapture && upEvent.pointerId !== undefined) {
                                  try { e.target.releasePointerCapture(upEvent.pointerId); } catch(err) {}
                                }
                                setIsDragging(false);
                                draggingRef.current = false;
                                document.removeEventListener('pointermove', onMove);
                                document.removeEventListener('pointerup', onUp);
                              };
                              document.addEventListener('pointermove', onMove, { passive: false });
                              document.addEventListener('pointerup', onUp);
                            }}
                          />
                        ))}
                      </>
                    )}
                    {/* Toolbar for the active box */}
                    {!isGenerating && activeBoxId === box.id && !readOnly && (
                      <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, display: 'flex', flexDirection: 'column', gap: '4px', zIndex: 100 }}>
                        {/* Row 1: Text Formatting Toolbar — only visible when editing text */}
                        {editingBoxId === box.id && box.type === 'text' && (
                          <div style={{ display: 'flex', gap: '4px', background: '#ffffff', padding: '6px 10px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', alignItems: 'center', width: 'max-content', alignSelf: 'flex-end', fontSize: '13px', fontFamily: "'Inter', -apple-system, sans-serif" }}>
                            {[
                              { label: 'B', title: 'Bold', style: { fontWeight: 'bold' }, format: 'bold' },
                              { label: 'I', title: 'Italic', style: { fontStyle: 'italic' }, format: 'italic' },
                              { label: 'U', title: 'Underline', style: { textDecoration: 'underline' }, format: 'underline' },
                              { label: 'S', title: 'Strikethrough', style: { textDecoration: 'line-through' }, format: 'strike' },
                            ].map(btn => (
                              <button
                                key={btn.format}
                                title={btn.title}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const editor = quillRef.current?.getEditor();
                                  if (!editor) return;
                                  const current = editor.getFormat();
                                  editor.format(btn.format, !current[btn.format]);
                                }}
                                style={{ ...btn.style, background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '13px', minWidth: '28px' }}
                              >{btn.label}</button>
                            ))}
                            <div style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
                            {/* Ordered list */}
                            <button title="Ordered List" onMouseDown={(e) => { e.preventDefault(); const ed = quillRef.current?.getEditor(); if (!ed) return; const cur = ed.getFormat(); ed.format('list', cur.list === 'ordered' ? false : 'ordered'); }} style={{ background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '13px' }}>1.</button>
                            {/* Bullet list */}
                            <button title="Bullet List" onMouseDown={(e) => { e.preventDefault(); const ed = quillRef.current?.getEditor(); if (!ed) return; const cur = ed.getFormat(); ed.format('list', cur.list === 'bullet' ? false : 'bullet'); }} style={{ background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '13px' }}>•</button>
                            <div style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
                            {/* Text color */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 6px' }}>
                              <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>A</span>
                              <input type="color" defaultValue="#000000" title="Text Color" onInput={(e) => { const ed = quillRef.current?.getEditor(); if (ed) ed.format('color', e.target.value); }} style={{ width: '18px', height: '18px', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }} />
                            </div>
                            {/* Background color */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 6px' }}>
                              <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>BG</span>
                              <input type="color" defaultValue="#ffffff" title="Highlight Color" onInput={(e) => { const ed = quillRef.current?.getEditor(); if (ed) ed.format('background', e.target.value); }} style={{ width: '18px', height: '18px', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }} />
                            </div>
                            <div style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
                            {/* Clear formatting */}
                            <button title="Clear Formatting" onMouseDown={(e) => { e.preventDefault(); const ed = quillRef.current?.getEditor(); if (ed) { const range = ed.getSelection(); if (range) ed.removeFormat(range.index, range.length); } }} style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>Tx</button>
                            {/* Done button */}
                            <button title="Done Editing" onClick={() => setEditingBoxId(null)} style={{ background: '#8b3dff', color: '#ffffff', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, marginLeft: '4px' }}>Done</button>
                          </div>
                        )}

                        {/* Row 2: Size, Font, Delete */}
                        <div style={{ display: 'flex', gap: '8px', background: '#ffffff', padding: '6px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', fontSize: '13px', fontFamily: "'Inter', -apple-system, sans-serif", whiteSpace: 'nowrap', alignItems: 'center' }}>
                          {/* Size */}
                          <div style={{ display: 'flex', alignItems: 'center', background: '#f3f4f6', padding: '2px 8px', borderRadius: '6px' }}>
                            <span style={{ fontSize: '12px', marginRight: '4px', color: '#6b7280', fontWeight: 500 }}>Size</span>
                            <input 
                              type="number" 
                              value={box.fontSize || 16} 
                              onChange={(e) => updateTextBox(box.id, 'fontSize', parseInt(e.target.value) || 16)}
                              style={{ width: '36px', border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', fontWeight: 600, color: '#111827' }}
                            />
                          </div>

                          {/* Font */}
                          <div style={{ display: 'flex', alignItems: 'center', background: '#f3f4f6', padding: '2px 8px', borderRadius: '6px' }}>
                            <span style={{ fontSize: '12px', marginRight: '4px', color: '#6b7280', fontWeight: 500 }}>Font</span>
                            <select 
                              value={box.fontFamily || 'Arial, sans-serif'} 
                              onChange={(e) => updateTextBox(box.id, 'fontFamily', e.target.value)}
                              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', fontWeight: 600, color: '#111827', cursor: 'pointer', maxWidth: '120px' }}
                            >
                              <option value="Arial, sans-serif" style={{ fontFamily: 'Arial, sans-serif' }}>Arial</option>
                              <option value="'Times New Roman', Times, serif" style={{ fontFamily: "'Times New Roman', Times, serif" }}>Times New Roman</option>
                              <option value="Georgia, serif" style={{ fontFamily: "Georgia, serif" }}>Georgia</option>
                              <option value="'Courier New', monospace" style={{ fontFamily: "'Courier New', monospace" }}>Courier New</option>
                              <option value="Verdana, sans-serif" style={{ fontFamily: "Verdana, sans-serif" }}>Verdana</option>
                              <option value="'Inter', sans-serif" style={{ fontFamily: "'Inter', sans-serif" }}>Inter</option>
                              <option value="'Roboto', sans-serif" style={{ fontFamily: "'Roboto', sans-serif" }}>Roboto</option>
                              <option value="'Montserrat', sans-serif" style={{ fontFamily: "'Montserrat', sans-serif" }}>Montserrat</option>
                              <option value="'Open Sans', sans-serif" style={{ fontFamily: "'Open Sans', sans-serif" }}>Open Sans</option>
                              <option value="'Lato', sans-serif" style={{ fontFamily: "'Lato', sans-serif" }}>Lato</option>
                              <option value="'Poppins', sans-serif" style={{ fontFamily: "'Poppins', sans-serif" }}>Poppins</option>
                              <option value="'Playfair Display', serif" style={{ fontFamily: "'Playfair Display', serif" }}>Playfair Display</option>
                              <option value="'Merriweather', serif" style={{ fontFamily: "'Merriweather', serif" }}>Merriweather</option>
                            </select>
                          </div>

                          {/* Table Specific Controls */}
                          {box.type === 'table' && (
                            <div style={{ display: 'flex', gap: '8px', paddingLeft: '8px', borderLeft: '1px solid #e5e7eb' }}>
                              {/* Alignment */}
                              <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '6px', overflow: 'hidden' }}>
                                <button onClick={(e) => { e.stopPropagation(); updateTextBox(box.id, 'textAlign', 'left'); }} style={{ background: box.textAlign === 'left' ? '#d1d5db' : 'transparent', color: '#374151', border: 'none', padding: '4px 8px', cursor: 'pointer' }} title="Align Left">⫷</button>
                                <button onClick={(e) => { e.stopPropagation(); updateTextBox(box.id, 'textAlign', 'center'); }} style={{ background: (!box.textAlign || box.textAlign === 'center') ? '#d1d5db' : 'transparent', color: '#374151', border: 'none', padding: '4px 8px', cursor: 'pointer' }} title="Align Center">≡</button>
                                <button onClick={(e) => { e.stopPropagation(); updateTextBox(box.id, 'textAlign', 'right'); }} style={{ background: box.textAlign === 'right' ? '#d1d5db' : 'transparent', color: '#374151', border: 'none', padding: '4px 8px', cursor: 'pointer' }} title="Align Right">⫸</button>
                              </div>

                              {/* Rows & Cols */}
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', background: '#ecfdf5', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1fae5' }}>
                                  <span style={{ fontSize: '12px', padding: '0 6px', color: '#059669', fontWeight: 600 }}>Row</span>
                                  <button onClick={(e) => { e.stopPropagation(); updateTextBox(box.id, 'data', [...box.data, Array(box.data[0].length).fill('')]); }} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: 'none', borderLeft: '1px solid #d1fae5', padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>+</button>
                                  <button onClick={(e) => { e.stopPropagation(); if (box.data.length > 1) updateTextBox(box.id, 'data', box.data.slice(0, -1)); }} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: 'none', borderLeft: '1px solid #d1fae5', padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>-</button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', background: '#f5f3ff', borderRadius: '6px', overflow: 'hidden', border: '1px solid #ede9fe' }}>
                                  <span style={{ fontSize: '12px', padding: '0 6px', color: '#7c3aed', fontWeight: 600 }}>Col</span>
                                  <button onClick={(e) => { e.stopPropagation(); updateTextBox(box.id, 'data', box.data.map(row => [...row, ''])); }} style={{ background: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', border: 'none', borderLeft: '1px solid #ede9fe', padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>+</button>
                                  <button onClick={(e) => { e.stopPropagation(); if (box.data[0].length > 1) updateTextBox(box.id, 'data', box.data.map(row => row.slice(0, -1))); }} style={{ background: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', border: 'none', borderLeft: '1px solid #ede9fe', padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>-</button>
                                </div>
                              </div>

                              {/* BG Color */}
                              <div style={{ display: 'flex', alignItems: 'center', background: '#fffbeb', borderRadius: '6px', overflow: 'hidden', border: '1px solid #fef3c7', padding: '2px 4px' }}>
                                <span style={{ fontSize: '12px', padding: '0 4px', color: '#d97706', fontWeight: 600 }}>BG</span>
                                <input type="color" value={box.backgroundColor || '#ffffff'} onChange={(e) => updateTextBox(box.id, 'backgroundColor', e.target.value)} style={{ border: 'none', width: '20px', height: '20px', padding: 0, cursor: 'pointer', background: 'transparent' }} title="Table Background Color" />
                              </div>
                            </div>
                          )}

                          {/* Delete Button */}
                          <div style={{ paddingLeft: '8px', borderLeft: '1px solid #e5e7eb' }}>
                            <button onClick={() => setTextBoxes(prev => prev.filter(b => b.id !== box.id))} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fee2e2', borderRadius: '6px', cursor: 'pointer', padding: '4px 8px', fontWeight: 500 }}>
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* The Content Editor */}
                    <div style={{ opacity: activeBoxId === box.id || !isGenerating ? 1 : 0.9, height: '100%' }}>
                      {box.type === 'table' ? (
                        <table style={{ width: '100%', height: '100%', borderCollapse: 'collapse', background: box.backgroundColor || 'transparent' }}>
                          <tbody>
                            {box.data.map((row, r) => (
                              <tr key={r}>
                                {row.map((cell, c) => (
                                  <td key={c} style={{ border: '1px solid #4b5563', padding: '0' }}>
                                    {activeBoxId === box.id && !isGenerating ? (
                                      <input
                                        type="text"
                                        value={cell}
                                        onChange={(e) => {
                                          const newData = [...box.data];
                                          newData[r][c] = e.target.value;
                                          updateTextBox(box.id, 'data', newData);
                                        }}
                                        style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', padding: '4px 8px', outline: 'none', boxSizing: 'border-box' }}
                                      />
                                    ) : (
                                      <div style={{ width: '100%', height: '100%', padding: '8px', fontSize: 'inherit', boxSizing: 'border-box', textAlign: box.textAlign || 'center', wordBreak: 'break-word', display: 'flex', alignItems: 'center', justifyContent: box.textAlign === 'left' ? 'flex-start' : box.textAlign === 'right' ? 'flex-end' : 'center' }}>
                                        {cell}
                                      </div>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : editingBoxId === box.id ? (
                          <div style={{ position: 'relative' }}>
                            <ReactQuill
                              ref={quillRef}
                              theme="snow"
                              value={box.text}
                              onChange={(content) => updateTextBox(box.id, 'text', content)}
                              modules={{ toolbar: false }}
                              style={{ background: 'white', borderRadius: '4px', minHeight: '80px', border: '1px solid #c4b5fd' }}
                            />
                          </div>
                      ) : (
                        <div
                          className="ql-editor"
                          dangerouslySetInnerHTML={{ __html: box.text || '<p>Double-click to edit</p>' }}
                          style={{ background: 'transparent', pointerEvents: 'none' }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </Document>
      </div>

      {/* Hidden file input for image uploading */}
      <input
        type="file"
        accept="image/*"
        ref={imageFileInputRef}
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />

      {/* Dummy Quill to keep the toolbar permanently visible even when no text box is active */}
      <div style={{ opacity: activeBoxId ? 0 : 1, pointerEvents: activeBoxId ? 'none' : 'auto', position: 'fixed', top: -9999, left: -9999, zIndex: 9999 }}>
        <ReactQuill theme="snow" value="" modules={activeModules} />
      </div>

      {/* Loading Overlay */}
      {isGenerating && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
          <div style={{ width: '60px', height: '60px', border: '6px solid rgba(255,255,255,0.2)', borderTop: '6px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <h2 style={{ marginTop: '24px', fontWeight: 'bold', letterSpacing: '1px' }}>Generating Proposal PDF...</h2>
          <p style={{ marginTop: '8px', color: '#d1d5db' }}>Please wait while we process and save your document.</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
}

export default ProposalGenerator;
